import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { CheckCircle2, RotateCcw, DollarSign, Handshake, Loader2, Search, ArrowUpDown } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/formatters';
import CofundedPayables from '../components/partner-ledger/CofundedPayables';   // 10/19: PFS / DIB owed to co-funded partners

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartnerLedger({ embedded = false }) {
  // 11/03 (Josh: consolidate pages): `embedded` renders the body without the app shell, inside a hub tab.
  const Wrap = embedded ? React.Fragment : Layout;
  const { getAuthHeaders } = useAuth();
  const [view, setView] = useState('cm');   // 'cm' = CM owed to partner (this ledger) | 'cofunded' = PFS / DIB owed on co-funded deals
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('owed');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('deal_id_number');
  const [sortDir, setSortDir] = useState(-1);
  const [partners, setPartners] = useState([]);
  const [busyId, setBusyId] = useState(null);
  // mark-paid dialog state
  const [payEntry, setPayEntry] = useState(null);
  const [payDate, setPayDate] = useState('');
  const [payRef, setPayRef] = useState('');
  const [payNote, setPayNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const pq = partnerFilter !== 'all' ? `&partner_contact_id=${partnerFilter}` : '';
      const res = await axios.get(`${API}/partner-commissions?status=${statusFilter}${pq}`, { headers: getAuthHeaders() });
      setEntries(res.data.entries || []);
      setTotals(res.data.totals_by_partner || {});
      setPartners(res.data.partners || []);
    } catch (e) {
      toast.error(`Failed to load ledger: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, statusFilter, partnerFilter]);

  useEffect(() => { load(); }, [load]);

  const openPay = (entry) => {
    setPayEntry(entry);
    setPayDate(new Date().toISOString().slice(0, 10));
    setPayRef('');
    setPayNote('');
  };

  const submitPay = async () => {
    if (!payEntry) return;
    setBusyId(payEntry.id);
    try {
      await axios.put(`${API}/partner-commissions/${payEntry.id}/mark-paid`, {
        paid_date: payDate ? `${payDate}T00:00:00` : null,
        reference: payRef || null,
        note: payNote || null,
      }, { headers: getAuthHeaders() });
      toast.success(`Marked paid — ${payEntry.deal_id_number} ${formatCurrency(payEntry.amount_owed)}`);
      setPayEntry(null);
      await load();
    } catch (e) {
      toast.error(`Mark paid failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const markWire = async (entry) => {
    const ref = window.prompt(`Wire received for #${entry.deal_id_number} (${formatCurrency(entry.wire_due)}). Reference (optional):`, '');
    if (ref === null) return;
    setBusyId(entry.id);
    try {
      await axios.put(`${API}/partner-commissions/${entry.id}/mark-wire-received`, {
        wire_reference: ref || null,
      }, { headers: getAuthHeaders() });
      toast.success('Wire marked received');
      await load();
    } catch (e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const unmarkWire = async (entry) => {
    if (!window.confirm(`Undo wire received on #${entry.deal_id_number}?`)) return;
    setBusyId(entry.id);
    try {
      await axios.put(`${API}/partner-commissions/${entry.id}/unmark-wire-received`, {}, { headers: getAuthHeaders() });
      await load();
    } catch (e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const unmark = async (entry) => {
    if (!window.confirm(`Move ${entry.deal_id_number} back to OWED?`)) return;
    setBusyId(entry.id);
    try {
      await axios.put(`${API}/partner-commissions/${entry.id}/unmark-paid`, {}, { headers: getAuthHeaders() });
      await load();
    } catch (e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => -d);
    else { setSortKey(key); setSortDir(1); }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = entries;
    if (q) {
      rows = rows.filter((e) =>
        String(e.deal_id_number || '').includes(q) ||
        String(e.deal_name || '').toLowerCase().includes(q) ||
        String(e.partner_name || '').toLowerCase().includes(q) ||
        String(e.reference || '').toLowerCase().includes(q));
    }
    const val = (e) => {
      const v = e[sortKey];
      if (sortKey === 'deal_id_number') return parseInt(v) || 0;
      if (typeof v === 'number') return v;
      return String(v || '').toLowerCase();
    };
    return [...rows].sort((a, b) => (val(a) < val(b) ? -1 : val(a) > val(b) ? 1 : 0) * sortDir);
  }, [entries, search, sortKey, sortDir]);

  const SortHead = ({ k, children, right }) => (
    <TableHead className={(right ? 'text-right ' : '') + 'cursor-pointer select-none hover:text-slate-900'}
      onClick={() => toggleSort(k)}>
      {children}{sortKey === k ? (sortDir === 1 ? ' \u2191' : ' \u2193') : <ArrowUpDown className="w-3 h-3 inline ml-1 opacity-40" />}
    </TableHead>
  );

  return (
    <Wrap>
      <div className="space-y-6" data-testid="partner-ledger-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Partner Commission Ledger</h1>
            <p className="text-sm text-slate-500 mt-1">Commissions owed to platform partners (Milvado, Enod, …) on participated deals</p>
            <div className="flex gap-2 mt-3">
              <Button variant={view === 'cm' ? 'default' : 'outline'} size="sm" onClick={() => setView('cm')}>CM owed to partner</Button>
              <Button variant={view === 'cofunded' ? 'default' : 'outline'} size="sm" onClick={() => setView('cofunded')}
                title="Co-funded deals: what PFS and DIB each owe the partner who paid the broker">Co-funded · PFS / DIB owed</Button>
            </div>
          </div>
          {view === 'cm' && (
          <div className="flex gap-2 items-center">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input className="pl-8 w-56" placeholder="Search deal, merchant, ref…"
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={partnerFilter} onValueChange={setPartnerFilter}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Partner" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Partners</SelectItem>
                {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {['owed', 'paid', 'all'].map((s) => (
              <Button key={s} variant={statusFilter === s ? 'default' : 'outline'} size="sm"
                onClick={() => setStatusFilter(s)}>{s.charAt(0).toUpperCase() + s.slice(1)}</Button>
            ))}
          </div>
          )}
        </div>

        {view === 'cofunded' ? <CofundedPayables /> : (<>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {Object.entries(totals).filter(([partner]) => {
            if (partnerFilter === 'all') return true;
            const sel = partners.find((p) => p.id === partnerFilter);
            return sel ? partner === sel.name : true;
          }).map(([partner, t]) => (
            <Card key={partner}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-500">{partner}</CardTitle>
                <Handshake className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-amber-600">{formatCurrency(t.open)}</div>
                <p className="text-xs text-slate-500 mt-1">CM open · {formatCurrency(t.paid)} paid · {formatCurrency(t.owed)} lifetime</p>
                {t.wire_due_open > 0 && <p className="text-xs font-medium text-red-600 mt-1">Wires due in: {formatCurrency(t.wire_due_open)}</p>}
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
              </div>
            ) : visible.length === 0 ? (
              <p className="text-center text-slate-400 py-12">No {statusFilter === 'all' ? '' : statusFilter} entries.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortHead k="deal_id_number">Deal</SortHead>
                    <SortHead k="deal_name">Merchant</SortHead>
                    <SortHead k="partner_name">Partner</SortHead>
                    <SortHead k="pfs_participation_pct" right>PFS %</SortHead>
                    <SortHead k="wire_due" right>Wire Due (from partner)</SortHead>
                    <SortHead k="amount_owed" right>CM Owed (to partner)</SortHead>
                    <SortHead k="status">Status</SortHead>
                    <SortHead k="paid_date">Paid / Reference</SortHead>
                    <TableHead className="text-right w-36">Synd Paid</TableHead>
                    <TableHead className="text-center w-14" title="Deal status: DEF = in Default, do not pay CM">Deal</TableHead>
                    <TableHead className="text-right w-36">CM Paid</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visible.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="font-medium">#{e.deal_id_number}</TableCell>
                      <TableCell className="text-slate-600 max-w-[220px] truncate">{e.deal_name}</TableCell>
                      <TableCell>{e.partner_name}</TableCell>
                      <TableCell className="text-right">{e.pfs_participation_pct != null ? `${e.pfs_participation_pct}%` : '—'}</TableCell>
                      <TableCell className="text-right">
                        {e.wire_due != null ? (
                          <span className={e.wire_status === 'received' ? 'text-green-600' : 'text-amber-600 font-semibold'}>
                            {formatCurrency(e.wire_due)}{e.wire_status === 'received' ? ' ✓' : ''}
                          </span>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-right font-semibold">{formatCurrency(e.amount_owed)}</TableCell>
                      <TableCell>
                        <Badge variant={e.status === 'paid' ? 'default' : e.status === 'waived' ? 'secondary' : 'destructive'}>{e.status}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {e.paid_date ? String(e.paid_date).slice(0, 10) : ''}
                        {e.reference ? <span className="font-mono ml-2">{e.reference}</span> : null}
                        {e.note ? <div className="text-slate-400">{e.note}</div> : null}
                      </TableCell>
                      <TableCell className="text-right w-36">
                        {e.wire_due == null ? (
                          <span className="text-slate-300">—</span>
                        ) : e.wire_status === 'received' ? (
                          <span className="text-green-600 text-xs inline-flex items-center justify-end gap-1">
                            <CheckCircle2 className="w-4 h-4" />{e.wire_date ? String(e.wire_date).slice(0, 10) : 'received'}
                            <Button size="sm" variant="ghost" className="h-6 px-1" title="Undo wire"
                              onClick={() => unmarkWire(e)} disabled={busyId === e.id}>
                              <RotateCcw className="w-3 h-3" />
                            </Button>
                          </span>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => markWire(e)} disabled={busyId === e.id}>
                            Wire In
                          </Button>
                        )}
                      </TableCell>
                      {/* 10/11 (Josh): red DEF when the deal is in Default — do not pay CM. */}
                      <TableCell className="text-center w-14" data-testid={`deal-status-${e.id}`}>
                        {e.deal_defaulted ? (
                          <span className="inline-block rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white"
                                title={`Deal status: ${e.deal_status}`}>DEF</span>
                        ) : (
                          <span className="text-slate-300" title={e.deal_status ? `Deal status: ${e.deal_status}` : ''}>—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right w-36">
                        {e.status === 'n/a' || !(e.amount_owed > 0) ? (
                          <span className="text-slate-300">—</span>
                        ) : e.status === 'paid' ? (
                          <span className="text-green-600 text-xs inline-flex items-center justify-end gap-1">
                            <CheckCircle2 className="w-4 h-4" />{e.paid_date ? String(e.paid_date).slice(0, 10) : 'paid'}
                            <Button size="sm" variant="ghost" className="h-6 px-1" title="Undo"
                              onClick={() => unmark(e)} disabled={busyId === e.id}>
                              <RotateCcw className="w-3 h-3" />
                            </Button>
                          </span>
                        ) : e.status === 'waived' ? (
                          <Badge variant="secondary">waived</Badge>
                        ) : (
                          <Button size="sm" onClick={() => openPay(e)} disabled={busyId === e.id}>
                            Mark Paid
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={!!payEntry} onOpenChange={(o) => { if (!o) setPayEntry(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Mark paid — #{payEntry?.deal_id_number} · {formatCurrency(payEntry?.amount_owed || 0)} to {payEntry?.partner_name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <label className="text-sm text-slate-600">Paid date</label>
                <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
              </div>
              <div>
                <label className="text-sm text-slate-600">Wire / payment reference</label>
                <Input placeholder="reference number" value={payRef} onChange={(e) => setPayRef(e.target.value)} />
              </div>
              <div>
                <label className="text-sm text-slate-600">Note (optional)</label>
                <Input placeholder="note" value={payNote} onChange={(e) => setPayNote(e.target.value)} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPayEntry(null)}>Cancel</Button>
              <Button onClick={submitPay} disabled={busyId === payEntry?.id}>
                {busyId === payEntry?.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DollarSign className="w-4 h-4 mr-2" />}
                Confirm Paid
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </>)}
      </div>
    </Wrap>
  );
}
